import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import PropTypes from 'prop-types';
import { useTranslation } from 'react-i18next';
import EntityFormSurface from '../EntityFormSurface';
import useExternalFormAddress from '../../../hooks/useExternalFormAddress';
import CloseButton from '../../atoms/CloseButton';
import Button from '../../atoms/Button';
import AddressAutocompleteInput from '../AddressAutocompleteInput';
import ConfirmDeleteDialog from '../ConfirmDeleteDialog';
import { eventService } from '../../../services/eventService';
import { uomService } from '../../../services/uomService';
import {
    buildTimezoneOptionsForValue,
    resolveViewerTimezone,
} from '../../../utils/usTimezones';
import { isCanonicalObjectId } from '../../../utils/scheduledEntity';
import { isoToZonedWallTime, zonedWallTimeToIso } from '../../../utils/zonedTime';
import { extractApiErrorMessage } from '../../../utils/apiErrors';
import { toAddressSelection } from '../../../utils/location';
import { SHAREABLE_OBJECT_VISIBILITY_OPTIONS } from '../PostVisibilityControl/options';
import {
    SCHEDULED_ENTITY_VISIBILITY_VALUE_SET,
    normalizeScheduledEntityFormVisibility,
    resolveScheduledEntityPayloadVisibility,
} from '../../../constants/scheduledEntityVisibility';
import './index.scss';

const JOIN_POLICY_OPTIONS = ['OPEN', 'APPROVAL_REQUIRED', 'CLOSED'];
const VISIBILITY_OPTIONS = SHAREABLE_OBJECT_VISIBILITY_OPTIONS.map((option) => option.value);
const TITLE_MAX = 255;
const DESCRIPTION_MAX = 4000;

function buildCreateEventRequestIdempotencyKey() {
    const timestamp = Date.now().toString(36);
    const randomSuffix = Math.random().toString(36).slice(2, 12);
    return `event-create-modal:create-event:${timestamp}:${randomSuffix}`.slice(0, 128);
}

function pad(value) {
    return String(value).padStart(2, '0');
}

// datetime-local strings are local-naive (no offset), e.g. "2026-04-23T19:30".
// We build the default with the viewer's local clock so the UI shows what the
// user expects, and we let the backend interpret it against the chosen tz.
function buildDefaultDateTime(offsetMinutes) {
    const now = new Date();
    now.setMinutes(now.getMinutes() + offsetMinutes);
    now.setSeconds(0, 0);
    return `${now.getFullYear()}-${pad(now.getMonth() + 1)}-${pad(now.getDate())}T${pad(now.getHours())}:${pad(now.getMinutes())}`;
}

function readString(value) {
    return typeof value === 'string' ? value.trim() : '';
}

function eventToSelectedAddress(event = {}) {
    const location = event?.location || {};
    return toAddressSelection(location, { primaryLabel: location.locationName });
}

function buildInitialFormState(event = null) {
    if (!event) {
        return {
            title: '',
            description: '',
            startAt: buildDefaultDateTime(60),
            endAt: buildDefaultDateTime(180),
            timezone: resolveViewerTimezone(),
            addressDisplay: '',
            venueName: '',
            visibility: 'PUBLIC',
            joinPolicy: 'OPEN',
            attendeeLimit: '',
        };
    }
    const tz = readString(event.timezone) || resolveViewerTimezone();
    const selectedAddress = eventToSelectedAddress(event);
    return {
        title: readString(event.title),
        description: typeof event.description === 'string' ? event.description : '',
        startAt: isoToZonedWallTime(event.startAt, tz) || buildDefaultDateTime(60),
        endAt: isoToZonedWallTime(event.endAt, tz) || buildDefaultDateTime(180),
        timezone: tz,
        addressDisplay: selectedAddress?.label || '',
        venueName: readString(event?.location?.locationName),
        visibility: normalizeScheduledEntityFormVisibility(event.visibility),
        joinPolicy: (readString(event.joinPolicy) || 'OPEN').toUpperCase(),
        attendeeLimit: Number.isFinite(event.attendeeLimit) && event.attendeeLimit > 0
            ? String(event.attendeeLimit)
            : '',
    };
}

const EventCreateModal = ({
    isOpen,
    embedded = false,
    mapAddress = undefined,
    onAddressSelected = undefined,
    locationPending = false,
    onBusyChange = undefined,
    onClose,
    mode = 'create',
    event: editingEvent = null,
    organizer = null,
    onCreated = undefined,
    onUpdated = undefined,
    onDeleted = undefined,
}) => {
    const isEditMode = mode === 'edit';
    const { t } = useTranslation();
    const [formState, setFormState] = useState(() => buildInitialFormState(isEditMode ? editingEvent : null));
    const [selectedAddress, setSelectedAddress] = useState(
        () => (isEditMode ? eventToSelectedAddress(editingEvent || {}) : null),
    );
    const [organizerObjectId, setOrganizerObjectId] = useState(null);
    const [organizerError, setOrganizerError] = useState(null);
    const [hasAttemptedOrganizerLookup, setHasAttemptedOrganizerLookup] = useState(false);
    const [isOrganizerResolving, setIsOrganizerResolving] = useState(false);
    const [submitError, setSubmitError] = useState(null);
    const [validationError, setValidationError] = useState(null);
    const [isSubmitting, setIsSubmitting] = useState(false);
    const [isDeleteDialogOpen, setIsDeleteDialogOpen] = useState(false);
    const [isDeleting, setIsDeleting] = useState(false);
    const [deleteError, setDeleteError] = useState(null);
    const [hasEditedVisibility, setHasEditedVisibility] = useState(false);
    const mountedRef = useRef(true);
    const organizerRequestRef = useRef(0);
    const createRequestIdempotencyKeyRef = useRef(null);
    const createRequestPayloadFingerprintRef = useRef(null);

    const createOrganizer = useMemo(() => {
        if (isEditMode || !organizer || typeof organizer !== 'object') {
            return null;
        }
        const objectType = readString(organizer.objectType).toUpperCase();
        const objectId = readString(organizer.objectId);
        if ((objectType !== 'USER' && objectType !== 'BAND') || !isCanonicalObjectId(objectId)) {
            return null;
        }
        return { objectType, objectId };
    }, [isEditMode, organizer]);

    useEffect(() => {
        mountedRef.current = true;
        return () => { mountedRef.current = false; };
    }, []);

    // Reset on open so re-opening doesn't leak prior input.
    useEffect(() => {
        if (!isOpen) return;
        organizerRequestRef.current += 1;
        createRequestIdempotencyKeyRef.current = null;
        createRequestPayloadFingerprintRef.current = null;
        setFormState(buildInitialFormState(isEditMode ? editingEvent : null));
        setSelectedAddress(isEditMode ? eventToSelectedAddress(editingEvent || {}) : null);
        setOrganizerObjectId(createOrganizer?.objectId || null);
        setOrganizerError(null);
        setSubmitError(null);
        setValidationError(null);
        setHasAttemptedOrganizerLookup(Boolean(createOrganizer));
        setIsOrganizerResolving(false);
        setIsDeleteDialogOpen(false);
        setIsDeleting(false);
        setDeleteError(null);
        setHasEditedVisibility(false);
    }, [isOpen, isEditMode, editingEvent, createOrganizer]);

    const resolveOrganizerObjectId = useCallback(() => {
        const requestId = organizerRequestRef.current + 1;
        organizerRequestRef.current = requestId;
        setHasAttemptedOrganizerLookup(true);
        setOrganizerError(null);
        setIsOrganizerResolving(true);

        return uomService.ensureCurrentUserObject()
            .then((response) => {
                if (!mountedRef.current || organizerRequestRef.current !== requestId) return null;
                const next = typeof response?.objectId === 'string' ? response.objectId.trim() : '';
                if (!isCanonicalObjectId(next)) {
                    setOrganizerObjectId(null);
                    setOrganizerError(t('eventsPage.createModal.errors.organizer'));
                    return null;
                }
                setOrganizerObjectId(next);
                return next;
            })
            .catch((error) => {
                if (!mountedRef.current || organizerRequestRef.current !== requestId) return null;
                console.error('Failed to ensure organizer object id', error);
                setOrganizerObjectId(null);
                setOrganizerError(t('eventsPage.createModal.errors.organizer'));
                return null;
            })
            .finally(() => {
                if (!mountedRef.current || organizerRequestRef.current !== requestId) return;
                setIsOrganizerResolving(false);
            });
    }, [t]);

    // Ensure the current user's UOM USER object exists (distinct from auth
    // userId per UOM rules). We intentionally re-run this on each open so auth
    // account switches without a full reload cannot reuse a stale organizer objectId.
    // Skip in edit mode — the event already has an owner; updateEvent does not
    // need an organizer objectId resolution roundtrip.
    useEffect(() => {
        if (!isOpen) return;
        if (isEditMode) return;
        if (createOrganizer) return;
        if (organizerObjectId) return;
        if (isOrganizerResolving) return;
        if (hasAttemptedOrganizerLookup) return;
        resolveOrganizerObjectId();
    }, [createOrganizer, hasAttemptedOrganizerLookup, isOpen, isEditMode, organizerObjectId, isOrganizerResolving, resolveOrganizerObjectId]);

    useExternalFormAddress({
        isOpen,
        address: mapAddress,
        isResolving: locationPending,
        setSelectedAddress,
        setFormState,
        setValidationError,
    });

    const updateField = useCallback((field) => (event) => {
        const value = event?.target?.value ?? '';
        setFormState((prev) => (prev[field] === value ? prev : { ...prev, [field]: value }));
        if (field === 'visibility') {
            setHasEditedVisibility(true);
        }
        setValidationError(null);
        setSubmitError(null);
    }, []);

    const handleAddressDisplayChange = useCallback((nextValue) => {
        setFormState((prev) => (prev.addressDisplay === nextValue ? prev : { ...prev, addressDisplay: nextValue }));
        setSelectedAddress((prev) => {
            if (!prev) return prev;
            return prev.label === nextValue ? prev : null;
        });
        setValidationError(null);
    }, []);

    const handleAddressSelect = useCallback((geocoded) => {
        setSelectedAddress(geocoded);
        onAddressSelected?.(geocoded);
        if (geocoded?.label) {
            setFormState((prev) => ({ ...prev, addressDisplay: geocoded.label }));
        }
        setValidationError(null);
    }, [onAddressSelected]);

    const validate = useCallback(() => {
        const title = formState.title.trim();
        if (!title) return t('eventsPage.createModal.errors.titleRequired');
        if (title.length > TITLE_MAX) return t('eventsPage.createModal.errors.titleTooLong', { max: TITLE_MAX });
        if (formState.description.length > DESCRIPTION_MAX) {
            return t('eventsPage.createModal.errors.descriptionTooLong', { max: DESCRIPTION_MAX });
        }
        const timezone = formState.timezone.trim();
        if (!timezone) return t('eventsPage.createModal.errors.timezoneRequired');
        // datetime-local values are wall-clock strings with no offset — we must
        // anchor them against the chosen event timezone, not the browser's, so
        // a 19:30 NYC show booked from LA persists as the correct Instant.
        const startIso = zonedWallTimeToIso(formState.startAt, timezone);
        const endIso = zonedWallTimeToIso(formState.endAt, timezone);
        if (!startIso || !endIso) return t('eventsPage.createModal.errors.scheduleInvalid');
        if (new Date(endIso).getTime() <= new Date(startIso).getTime()) {
            return t('eventsPage.createModal.errors.scheduleOrder');
        }
        if (!selectedAddress
            || !selectedAddress.cityName
            || !selectedAddress.countryName
            || typeof selectedAddress.latitude !== 'number'
            || typeof selectedAddress.longitude !== 'number') {
            return t('eventsPage.createModal.errors.addressRequired');
        }
        if (formState.attendeeLimit !== '' && (!/^\d+$/.test(formState.attendeeLimit) || Number(formState.attendeeLimit) <= 0)) {
            return t('eventsPage.createModal.errors.attendeeLimit');
        }
        if (!SCHEDULED_ENTITY_VISIBILITY_VALUE_SET.has(formState.visibility)) {
            return t('eventsPage.createModal.errors.visibilityInvalid', 'Choose Public, Friends, or Private.');
        }
        if (isEditMode) {
            const organizerType = readString(editingEvent?.organizer?.objectType).toUpperCase();
            const organizerId = readString(editingEvent?.organizer?.objectId);
            if (!organizerType || !isCanonicalObjectId(organizerId)) {
                return t('eventsPage.createModal.errors.organizer');
            }
        }
        return null;
    }, [editingEvent, formState, isEditMode, selectedAddress, t]);

    const buildPayload = useCallback(() => {
        const timezone = formState.timezone.trim();
        const startIso = zonedWallTimeToIso(formState.startAt, timezone);
        const endIso = zonedWallTimeToIso(formState.endAt, timezone);
        const trimmedDescription = formState.description.trim();
        const trimmedVenueName = formState.venueName.trim();
        const attendeeLimitValue = formState.attendeeLimit.trim();
        const countryCode = (selectedAddress.countryCode || '').slice(0, 2).toLowerCase();

        const payloadOrganizerObjectType = isEditMode
            ? readString(editingEvent?.organizer?.objectType).toUpperCase()
            : (createOrganizer?.objectType || 'USER');
        const payloadOrganizerObjectId = isEditMode
            ? readString(editingEvent?.organizer?.objectId)
            : (createOrganizer?.objectId || organizerObjectId);
        const venueObjectId = readString(editingEvent?.venue?.objectId);

        const payload = {
            title: formState.title.trim(),
            description: trimmedDescription || null,
            startAt: startIso,
            endAt: endIso,
            timezone,
            locationName: trimmedVenueName || null,
            addressLine: selectedAddress.addressLine || null,
            city: selectedAddress.cityName,
            stateOrRegion: selectedAddress.regionName || null,
            country: selectedAddress.countryName,
            countryCode: countryCode || null,
            latitude: selectedAddress.latitude,
            longitude: selectedAddress.longitude,
            joinPolicy: formState.joinPolicy,
            visibility: resolveScheduledEntityPayloadVisibility(
                formState.visibility,
                isEditMode ? editingEvent?.visibility : null,
                hasEditedVisibility,
            ),
            attendeeLimit: attendeeLimitValue === '' ? null : Number(attendeeLimitValue),
            status: isEditMode ? (readString(editingEvent?.status) || 'PUBLISHED') : 'PUBLISHED',
            organizerObjectType: payloadOrganizerObjectType,
            organizerObjectId: payloadOrganizerObjectId,
        };

        if (venueObjectId) {
            payload.venueObjectId = venueObjectId;
        }

        return payload;
    }, [createOrganizer, formState, organizerObjectId, selectedAddress, isEditMode, editingEvent, hasEditedVisibility]);

    const getCreateRequestIdempotencyKey = useCallback((payload) => {
        const payloadFingerprint = JSON.stringify(payload);

        if (!createRequestIdempotencyKeyRef.current
            || createRequestPayloadFingerprintRef.current !== payloadFingerprint) {
            createRequestIdempotencyKeyRef.current = buildCreateEventRequestIdempotencyKey();
            createRequestPayloadFingerprintRef.current = payloadFingerprint;
        }
        return createRequestIdempotencyKeyRef.current;
    }, []);

    const handleSubmit = async (event) => {
        event?.preventDefault();
        if (isSubmitting || isDeleting || locationPending) return;
        if (!isEditMode && (!organizerObjectId || (organizer && !createOrganizer))) {
            setSubmitError(organizerError || t('eventsPage.createModal.errors.organizer'));
            return;
        }
        const message = validate();
        if (message) {
            setValidationError(message);
            return;
        }
        setValidationError(null);
        setSubmitError(null);
        setIsSubmitting(true);
        try {
            const payload = buildPayload();
            let saved;
            if (isEditMode) {
                saved = await eventService.updateEvent(editingEvent.id, payload);
            } else {
                saved = await eventService.createEvent(payload, {
                    headers: {
                        'Idempotency-Key': getCreateRequestIdempotencyKey(payload),
                    },
                });
            }
            if (!mountedRef.current) return;
            createRequestIdempotencyKeyRef.current = null;
            createRequestPayloadFingerprintRef.current = null;
            if (isEditMode) {
                if (typeof onUpdated === 'function') onUpdated(saved);
            } else if (typeof onCreated === 'function') {
                onCreated(saved);
            }
            onClose?.();
        } catch (error) {
            if (!mountedRef.current) return;
            console.error(`Failed to ${isEditMode ? 'update' : 'create'} event`, error);
            setSubmitError(extractApiErrorMessage(
                error,
                t(isEditMode ? 'eventsPage.editModal.errors.update' : 'eventsPage.createModal.errors.submit'),
            ));
        } finally {
            if (mountedRef.current) setIsSubmitting(false);
        }
    };

    const handleClose = useCallback(() => {
        if (isSubmitting || isDeleting) return;
        onClose?.();
    }, [isSubmitting, isDeleting, onClose]);

    const handleOpenDeleteDialog = useCallback(() => {
        if (isSubmitting || isDeleting) return;
        setDeleteError(null);
        setIsDeleteDialogOpen(true);
    }, [isSubmitting, isDeleting]);

    const handleCancelDelete = useCallback(() => {
        if (isDeleting) return;
        setIsDeleteDialogOpen(false);
        setDeleteError(null);
    }, [isDeleting]);

    const handleConfirmDelete = useCallback(async () => {
        if (!isEditMode || !editingEvent?.id || isDeleting) return;
        setIsDeleting(true);
        setDeleteError(null);
        try {
            await eventService.deleteEvent(editingEvent.id);
            if (!mountedRef.current) return;
            setIsDeleteDialogOpen(false);
            if (typeof onDeleted === 'function') onDeleted(editingEvent);
            onClose?.();
        } catch (error) {
            if (!mountedRef.current) return;
            console.error('Failed to delete event', error);
            setDeleteError(extractApiErrorMessage(error, t('eventsPage.editModal.errors.delete')));
        } finally {
            if (mountedRef.current) setIsDeleting(false);
        }
    }, [isEditMode, editingEvent, isDeleting, onDeleted, onClose, t]);

    const handleRetryOrganizerResolution = useCallback(() => {
        if (isSubmitting || isOrganizerResolving) return;
        setSubmitError(null);
        resolveOrganizerObjectId();
    }, [isOrganizerResolving, isSubmitting, resolveOrganizerObjectId]);

    const timezoneOptions = useMemo(
        () => buildTimezoneOptionsForValue(formState.timezone, t),
        [formState.timezone, t],
    );

    const joinPolicyOptions = useMemo(
        () => JOIN_POLICY_OPTIONS.map((value) => ({
            value,
            label: t(`eventsPage.createModal.joinPolicy.${value.toLowerCase()}`),
        })),
        [t],
    );

    if (!isOpen) return null;

    const translationRoot = isEditMode ? 'eventsPage.editModal' : 'eventsPage.createModal';
    const errorMessage = submitError || validationError || (!isEditMode ? organizerError : null);

    return (
        <EntityFormSurface
            embedded={embedded}
            busy={isSubmitting || isDeleting}
            onBusyChange={onBusyChange}
            onClose={handleClose}
            closeOnEsc={!isSubmitting && !isDeleteDialogOpen}
        >
            <form
                className={`event-create-modal${embedded ? ' event-create-modal--embedded' : ''}`}
                role={embedded ? undefined : 'dialog'}
                aria-modal={embedded ? undefined : true}
                aria-labelledby="event-create-modal-title"
                onSubmit={handleSubmit}
            >
                <header className="event-create-modal__hero">
                    <div className="event-create-modal__hero-overlay" aria-hidden="true" />
                    <div className="event-create-modal__hero-top">
                        <CloseButton
                            ariaLabel={t(`${translationRoot}.close`)}
                            onClick={handleClose}
                            className="event-create-modal__close"
                        />
                    </div>
                    <div className="event-create-modal__hero-bottom">
                        <span className="event-create-modal__chip">
                            {t(`${translationRoot}.eyebrow`)}
                        </span>
                        <h2 id="event-create-modal-title" className="event-create-modal__title">
                            {t(`${translationRoot}.title`)}
                        </h2>
                        <p className="event-create-modal__subtitle">
                            {t(`${translationRoot}.subtitle`)}
                        </p>
                    </div>
                </header>

                <div className="event-create-modal__body">
                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('eventsPage.createModal.sections.basics')}
                        </legend>
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-title" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.titleLabel')}
                                <span className="event-create-modal__required" aria-hidden="true">*</span>
                            </label>
                            <input
                                id="event-create-title"
                                type="text"
                                className="event-create-modal__input"
                                value={formState.title}
                                onChange={updateField('title')}
                                placeholder={t('eventsPage.createModal.fields.titlePlaceholder')}
                                maxLength={TITLE_MAX}
                                disabled={isSubmitting}
                                required
                            />
                        </div>
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-description" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.descriptionLabel')}
                            </label>
                            <textarea
                                id="event-create-description"
                                className="event-create-modal__textarea"
                                value={formState.description}
                                onChange={updateField('description')}
                                placeholder={t('eventsPage.createModal.fields.descriptionPlaceholder')}
                                maxLength={DESCRIPTION_MAX}
                                rows={4}
                                disabled={isSubmitting}
                            />
                        </div>
                    </fieldset>

                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('eventsPage.createModal.sections.schedule')}
                        </legend>
                        <div className="event-create-modal__row event-create-modal__row--two">
                            <div className="event-create-modal__field">
                                <label htmlFor="event-create-start" className="event-create-modal__label">
                                    {t('eventsPage.createModal.fields.startLabel')}
                                    <span className="event-create-modal__required" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="event-create-start"
                                    type="datetime-local"
                                    className="event-create-modal__input"
                                    value={formState.startAt}
                                    onChange={updateField('startAt')}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>
                            <div className="event-create-modal__field">
                                <label htmlFor="event-create-end" className="event-create-modal__label">
                                    {t('eventsPage.createModal.fields.endLabel')}
                                    <span className="event-create-modal__required" aria-hidden="true">*</span>
                                </label>
                                <input
                                    id="event-create-end"
                                    type="datetime-local"
                                    className="event-create-modal__input"
                                    value={formState.endAt}
                                    onChange={updateField('endAt')}
                                    disabled={isSubmitting}
                                    required
                                />
                            </div>
                        </div>
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-tz" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.timezoneLabel')}
                                <span className="event-create-modal__required" aria-hidden="true">*</span>
                            </label>
                            <select
                                id="event-create-tz"
                                className="event-create-modal__select"
                                value={formState.timezone}
                                onChange={updateField('timezone')}
                                disabled={isSubmitting}
                                required
                            >
                                {timezoneOptions.map((option) => (
                                    <option key={option.value} value={option.value}>
                                        {option.label}
                                    </option>
                                ))}
                            </select>
                            <p className="event-create-modal__hint">
                                {t('eventsPage.createModal.fields.timezoneHint')}
                            </p>
                        </div>
                    </fieldset>

                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('eventsPage.createModal.sections.location')}
                        </legend>
                        <AddressAutocompleteInput
                            id="event-create-address"
                            label={t('eventsPage.createModal.fields.addressLabel')}
                            placeholder={t('eventsPage.createModal.fields.addressPlaceholder')}
                            value={formState.addressDisplay}
                            onChange={handleAddressDisplayChange}
                            onSelect={handleAddressSelect}
                            countryCode="US"
                            disabled={isSubmitting || locationPending}
                            required
                            preventEnterWithoutSelection
                            helpText={selectedAddress
                                ? t('eventsPage.createModal.fields.addressResolved', {
                                    city: selectedAddress.cityName || '',
                                    country: selectedAddress.countryName || '',
                                })
                                : t('eventsPage.createModal.fields.addressHint')}
                            loadingText={t('eventsPage.createModal.fields.addressLoading')}
                            emptyText={t('eventsPage.createModal.fields.addressEmpty')}
                            className="event-create-modal__address"
                        />
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-venue" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.venueLabel')}
                            </label>
                            <input
                                id="event-create-venue"
                                type="text"
                                className="event-create-modal__input"
                                value={formState.venueName}
                                onChange={updateField('venueName')}
                                placeholder={t('eventsPage.createModal.fields.venuePlaceholder')}
                                maxLength={255}
                                disabled={isSubmitting}
                            />
                        </div>
                    </fieldset>

                    <fieldset className="event-create-modal__section">
                        <legend className="event-create-modal__section-title">
                            {t('eventsPage.createModal.sections.access')}
                        </legend>
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-visibility" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.visibilityLabel')}
                            </label>
                            <select
                                id="event-create-visibility"
                                className="event-create-modal__select"
                                value={formState.visibility}
                                onChange={updateField('visibility')}
                                disabled={isSubmitting}
                            >
                                {VISIBILITY_OPTIONS.map((value) => (
                                    <option key={value} value={value}>
                                        {t(`eventsPage.createModal.visibility.${value.toLowerCase()}`)}
                                    </option>
                                ))}
                            </select>
                        </div>
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-policy" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.joinPolicyLabel')}
                            </label>
                            <select
                                id="event-create-policy"
                                className="event-create-modal__select"
                                value={formState.joinPolicy}
                                onChange={updateField('joinPolicy')}
                                disabled={isSubmitting}
                            >
                                {joinPolicyOptions.map((option) => (
                                    <option key={option.value} value={option.value}>{option.label}</option>
                                ))}
                            </select>
                        </div>
                        <div className="event-create-modal__field">
                            <label htmlFor="event-create-limit" className="event-create-modal__label">
                                {t('eventsPage.createModal.fields.attendeeLimitLabel')}
                            </label>
                            <input
                                id="event-create-limit"
                                type="number"
                                min="1"
                                inputMode="numeric"
                                className="event-create-modal__input"
                                value={formState.attendeeLimit}
                                onChange={updateField('attendeeLimit')}
                                placeholder={t('eventsPage.createModal.fields.attendeeLimitPlaceholder')}
                                disabled={isSubmitting}
                            />
                        </div>
                    </fieldset>

                    {errorMessage ? (
                        <div className="event-create-modal__error" role="alert">
                            <p>{errorMessage}</p>
                            {organizerError && !organizerObjectId ? (
                                <Button
                                    type="button"
                                    variant="secondary"
                                    shape="pill"
                                    size="small"
                                    onClick={handleRetryOrganizerResolution}
                                    disabled={isSubmitting || isOrganizerResolving}
                                >
                                    {t('eventsPage.actions.retry')}
                                </Button>
                            ) : null}
                        </div>
                    ) : null}
                </div>

                <footer className="event-create-modal__actions event-create-modal__actions--with-delete">
                    {isEditMode ? (
                        <button
                            type="button"
                            className="event-create-modal__delete"
                            onClick={handleOpenDeleteDialog}
                            disabled={isSubmitting || isDeleting}
                        >
                            {t('eventsPage.editModal.actions.delete')}
                        </button>
                    ) : null}
                    <div className="event-create-modal__actions-end">
                        <Button
                            type="button"
                            variant="secondary"
                            shape="pill"
                            size="medium"
                            onClick={handleClose}
                            disabled={isSubmitting || isDeleting}
                            className="event-create-modal__cancel"
                        >
                            {t('eventsPage.createModal.actions.cancel')}
                        </Button>
                        <Button
                            type="submit"
                            variant="gradient"
                            shape="pill"
                            size="medium"
                            disabled={
                                locationPending || isSubmitting
                                || isDeleting
                                || (!isEditMode && (isOrganizerResolving || !organizerObjectId || (organizer && !createOrganizer)))
                            }
                            className="event-create-modal__submit"
                        >
                            {isSubmitting
                                ? t('eventsPage.createModal.actions.submitting')
                                : t(`${translationRoot}.actions.submit`)}
                        </Button>
                    </div>
                </footer>
            </form>
            {isEditMode ? (
                <ConfirmDeleteDialog
                    isOpen={isDeleteDialogOpen}
                    title={t('eventsPage.editModal.deleteDialog.title')}
                    description={t('eventsPage.editModal.deleteDialog.description')}
                    targetName={readString(editingEvent?.title) || null}
                    confirmLabel={t('eventsPage.editModal.deleteDialog.confirm')}
                    cancelLabel={t('eventsPage.editModal.deleteDialog.cancel')}
                    isSubmitting={isDeleting}
                    errorMessage={deleteError}
                    onConfirm={handleConfirmDelete}
                    onCancel={handleCancelDelete}
                />
            ) : null}
        </EntityFormSurface>
    );
};

EventCreateModal.propTypes = {
    isOpen: PropTypes.bool.isRequired,
    embedded: PropTypes.bool,
    mapAddress: PropTypes.object,
    onAddressSelected: PropTypes.func,
    locationPending: PropTypes.bool,
    onBusyChange: PropTypes.func,
    onClose: PropTypes.func.isRequired,
    mode: PropTypes.oneOf(['create', 'edit']),
    organizer: PropTypes.shape({
        objectType: PropTypes.oneOf(['USER', 'BAND', 'user', 'band']),
        objectId: PropTypes.string,
        name: PropTypes.string,
    }),
    event: PropTypes.shape({
        id: PropTypes.string,
        title: PropTypes.string,
        description: PropTypes.string,
        status: PropTypes.string,
        startAt: PropTypes.string,
        endAt: PropTypes.string,
        timezone: PropTypes.string,
        visibility: PropTypes.string,
        joinPolicy: PropTypes.string,
        attendeeLimit: PropTypes.number,
        organizer: PropTypes.shape({
            objectType: PropTypes.string,
            objectId: PropTypes.string,
            name: PropTypes.string,
        }),
        venue: PropTypes.shape({
            objectId: PropTypes.string,
        }),
        location: PropTypes.shape({
            locationName: PropTypes.string,
            addressLine: PropTypes.string,
            city: PropTypes.string,
            stateOrRegion: PropTypes.string,
            country: PropTypes.string,
            countryCode: PropTypes.string,
            latitude: PropTypes.number,
            longitude: PropTypes.number,
        }),
    }),
    onCreated: PropTypes.func,
    onUpdated: PropTypes.func,
    onDeleted: PropTypes.func,
};

export default EventCreateModal;
